"use client";

/**
 * ErpSidebar — 240px fixed left sidebar (1:1 mockup port).
 *
 * Mockup: docs/mockups/erp-skeleton-v1-popunjen.html (1710 lin, LOCKED).
 * Sekcije: Pregled, Prodaja, Nabavka, Partneri, Proizvodi, Magacin, Banka,
 *          Podešavanja. Active link highlight kroz Next.js usePathname.
 *
 * Pravila:
 *  - PRAVILO 11: nema hover open — collapse je klik
 *  - PRAVILO 12: CSS vars only, dark mode, Manrope/Inter, crnogorski latinica
 *  - Grozd logo — koristi se u topbar-u; sidebar ima ulogu+tenant pill
 */

import * as React from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  LayoutGrid,
  Activity,
  FileText,
  Receipt,
  RefreshCw,
  Package,
  Inbox,
  Send,
  Users,
  UserPlus,
  PackageOpen,
  Tag,
  Coins,
  Box,
  Truck,
  ClipboardCheck,
  Building2,
  CreditCard,
  Wallet,
  Calculator,
  TrendingUp,
  Shield,
  Settings,
  Sun,
  Moon,
} from "@grozd/icons";
import type { LucideIcon } from "@grozd/icons";
import { isHrefEnabled } from "@/lib/flags";

interface NavItem {
  label: string;
  href: string;
  icon: LucideIcon;
  badge?: string | number;
  kbd?: string;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const SECTIONS: NavSection[] = [
  {
    title: "Pregled",
    items: [
      { label: "Dashboard", href: "/", icon: LayoutGrid, kbd: "G D" },
      { label: "Aktivnost", href: "/aktivnost", icon: Activity, badge: 3 },
    ],
  },
  {
    title: "Prodaja",
    items: [
      { label: "Fakture izlazne", href: "/fakture", icon: FileText },
      { label: "Predračuni", href: "/fakture/predracuni", icon: Receipt },
      { label: "Recurring", href: "/fakture/recurring", icon: RefreshCw },
    ],
  },
  {
    title: "Nabavka",
    items: [
      { label: "Fakture ulazne", href: "/fakture/ulazne", icon: Inbox },
      { label: "AI parser", href: "/fakture/ai-parser", icon: Activity },
      { label: "EFI uvoz", href: "/fakture/efi-uvoz", icon: Send },
    ],
  },
  {
    title: "Partneri",
    items: [
      { label: "Kupci / dobavljači", href: "/partneri", icon: Users },
      { label: "Hijerarhija", href: "/partneri/hijerarhija", icon: Building2 },
      { label: "Uvoz", href: "/partneri/uvoz", icon: UserPlus },
    ],
  },
  {
    title: "Proizvodi",
    items: [
      { label: "Lista", href: "/artikli", icon: Package },
      { label: "Cjenovnik", href: "/artikli/cjenovnik", icon: Tag },
      { label: "Akciza", href: "/artikli/akciza", icon: Coins },
      { label: "Varijante", href: "/artikli/kompleti", icon: Box },
    ],
  },
  {
    title: "Magacin",
    items: [
      { label: "Zalihe", href: "/magacin/zalihe", icon: PackageOpen },
      { label: "Prijemnica", href: "/magacin/prijemnica/nova", icon: Inbox },
      { label: "Otpremnica", href: "/magacin/otpremnica/nova", icon: Send },
      { label: "Transferi", href: "/magacin/transferi", icon: Truck },
      { label: "Popis", href: "/magacin/popis", icon: ClipboardCheck },
    ],
  },
  {
    title: "Banka",
    items: [
      { label: "Izvodi", href: "/banka/izvodi", icon: CreditCard },
      { label: "Reconciliation", href: "/banka/reconciliation", icon: TrendingUp },
      { label: "Blagajna", href: "/banka/blagajna", icon: Wallet },
      { label: "Transakcije", href: "/banka/transakcije", icon: Activity },
      { label: "POS", href: "/banka/pos-merchant", icon: Calculator },
    ],
  },
  {
    title: "Podešavanja",
    items: [
      { label: "Podešavanja", href: "/podesavanja", icon: Settings },
      { label: "Sigurnost", href: "/podesavanja/sigurnost", icon: Shield },
    ],
  },
];

/**
 * PILOT filter — van-pilot moduli (magacin, banka, …) se sakrivaju iz
 * navigacije kad je PILOT_MODE ON. Sekcije koje ostanu prazne se izbacuju.
 */
const VISIBLE_SECTIONS: NavSection[] = SECTIONS.map((section) => ({
  ...section,
  items: section.items.filter((item) => isHrefEnabled(item.href)),
})).filter((section) => section.items.length > 0);

interface ErpSidebarProps {
  /** Tenant ime, default "DOT PRO" */
  tenantName?: string;
  /** Korisnička uloga, default "Tenant Admin" */
  roleName?: string;
}

export function ErpSidebar({
  tenantName = "DOT PRO",
  roleName = "Tenant Admin",
}: ErpSidebarProps) {
  const pathname = usePathname();
  const [theme, setTheme] = React.useState<"light" | "dark">("light");

  // Init theme from localStorage / data-theme
  React.useEffect(() => {
    const stored = localStorage.getItem("grozd-theme");
    if (stored === "dark" || stored === "light") {
      setTheme(stored);
      document.documentElement.setAttribute("data-theme", stored);
    } else {
      const current = document.documentElement.getAttribute("data-theme");
      if (current === "dark") setTheme("dark");
    }
  }, []);

  const toggleTheme = React.useCallback(() => {
    const next = theme === "light" ? "dark" : "light";
    setTheme(next);
    document.documentElement.setAttribute("data-theme", next);
    localStorage.setItem("grozd-theme", next);
  }, [theme]);

  const isActive = React.useCallback(
    (href: string) => {
      if (href === "/") return pathname === "/";
      return pathname === href || pathname.startsWith(href + "/");
    },
    [pathname]
  );

  return (
    <aside
      className="erp-sb"
      aria-label="Bočna navigacija"
      style={{
        width: 240,
        flexShrink: 0,
        background: "var(--surface-card)",
        borderRight: "1px solid var(--n100)",
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 56px)",
        position: "sticky",
        top: 56,
        overflow: "hidden",
      }}
    >
      {/* Role pill */}
      <div
        className="flex items-center gap-2 px-4 pt-3.5 pb-3"
        style={{ borderBottom: "1px solid var(--n100)" }}
      >
        <span
          className="font-brand"
          style={{
            padding: "4px 10px",
            borderRadius: 6,
            fontSize: 10.5,
            fontWeight: 800,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            background: "linear-gradient(135deg, #FBBF24, #F97316)",
            color: "#fff",
            boxShadow: "0 1px 3px rgba(217,119,6,0.3)",
          }}
        >
          {roleName}
        </span>
        <span
          className="font-body"
          style={{ fontSize: 11.5, color: "var(--n400)", fontWeight: 500 }}
        >
          {tenantName}
        </span>
      </div>

      {/* Nav */}
      <nav
        className="erp-sb__nav"
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "10px 8px 4px",
        }}
      >
        {VISIBLE_SECTIONS.map((section) => (
          <div key={section.title} style={{ marginBottom: 14 }}>
            <div
              className="font-brand"
              style={{
                padding: "10px 12px 6px",
                fontSize: 10.5,
                fontWeight: 800,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "var(--n400)",
              }}
            >
              {section.title}
            </div>
            {section.items.map((item) => {
              const active = isActive(item.href);
              const Icon = item.icon;
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className="erp-sb__item"
                  data-active={active ? "true" : undefined}
                  aria-current={active ? "page" : undefined}
                  style={{
                    height: 38,
                    padding: "0 12px",
                    borderRadius: 10,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    fontSize: 13,
                    fontWeight: active ? 600 : 500,
                    color: active ? "var(--primary)" : "var(--n600)",
                    background: active
                      ? "linear-gradient(90deg, var(--p100), transparent 80%)"
                      : "transparent",
                    cursor: "pointer",
                    transition: "all 120ms cubic-bezier(0.22,1,0.36,1)",
                    position: "relative",
                    textDecoration: "none",
                  }}
                >
                  {active && (
                    <span
                      aria-hidden
                      style={{
                        position: "absolute",
                        left: 0,
                        top: 6,
                        bottom: 6,
                        width: 3,
                        background: "var(--primary)",
                        borderRadius: "0 3px 3px 0",
                      }}
                    />
                  )}
                  <Icon size={18} strokeWidth={1.8} />
                  <span style={{ flex: 1 }}>{item.label}</span>
                  {item.badge !== undefined && (
                    <span
                      style={{
                        marginLeft: "auto",
                        fontSize: 10,
                        fontWeight: 700,
                        padding: "2px 7px",
                        borderRadius: 6,
                        background: active
                          ? "var(--primary)"
                          : "var(--danger-light)",
                        color: active ? "#fff" : "var(--danger)",
                      }}
                    >
                      {item.badge}
                    </span>
                  )}
                  {item.kbd && item.badge === undefined && (
                    <span
                      className="font-mono"
                      style={{
                        marginLeft: "auto",
                        fontSize: 10,
                        color: "var(--n400)",
                      }}
                    >
                      {item.kbd}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Bottom: version + theme toggle */}
      <div
        className="flex items-center justify-between"
        style={{
          padding: "12px 14px",
          borderTop: "1px solid var(--n100)",
          fontSize: 11,
          color: "var(--n400)",
        }}
      >
        <span className="font-mono">v3.0.0</span>
        <button
          type="button"
          onClick={toggleTheme}
          className="flex items-center gap-1.5"
          style={{
            cursor: "pointer",
            padding: "4px 8px",
            borderRadius: 6,
            background: "transparent",
            border: "none",
            color: "var(--n400)",
            fontSize: 11,
            transition: "all 120ms",
          }}
          aria-label="Promijeni temu"
        >
          {theme === "light" ? <Moon size={14} /> : <Sun size={14} />}
          <span>Tema</span>
        </button>
      </div>

      {/* Hover styles via inline <style> for sidebar items */}
      <style jsx>{`
        :global(.erp-sb__item):hover:not([data-active]) {
          background: var(--tint) !important;
          color: var(--primary) !important;
        }
        :global(.erp-sb__nav)::-webkit-scrollbar {
          width: 6px;
        }
        :global(.erp-sb__nav)::-webkit-scrollbar-thumb {
          background: var(--n200);
          border-radius: 3px;
        }
      `}</style>
    </aside>
  );
}

export default ErpSidebar;
